<div class="p-6 max-h-[80vh] overflow-auto">
  <h2 mat-dialog-title class="text-xl font-semibold mb-2">Submitted Bids</h2>

  <div *ngIf="loading" class="py-8 text-center">
    <mat-spinner diameter="40" class="mx-auto"></mat-spinner>
    <p class="mt-4 text-gray-600">Loading bids...</p>
  </div>

  <div *ngIf="!loading && data" class="space-y-4">
    <div class="mb-4 p-3 bg-gray-50 rounded-lg flex justify-between items-center">
      <p class="text-sm text-gray-600">
        <strong>Project:</strong> {{ data.invitation.project_name }} |
        <strong>BOQ:</strong> {{ data.invitation.boq_code }} - {{ data.invitation.boq_name }}
      </p>
      <button mat-stroked-button color="accent" (click)="openDeviationReport()" *ngIf="data.responses?.length">
        <mat-icon class="mr-1" style="font-size:18px;width:18px;height:18px">assessment</mat-icon>
        Deviation Report
      </button>
    </div>

    <!-- Bidding Invitation Stats -->
    <div *ngIf="data.stats" class="mb-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
      <h3 class="font-semibold text-gray-800 mb-3 flex items-center gap-2">
        <mat-icon style="font-size:20px;width:20px;height:20px">people</mat-icon>
        Bidding Invitation Stats
      </h3>
      <div class="grid grid-cols-3 gap-4 mb-4">
        <div class="text-center p-2 rounded bg-white">
          <p class="text-2xl font-bold text-gray-800">{{ data.stats.total_invited }}</p>
          <p class="text-xs text-gray-600">Total Invited</p>
        </div>
        <div class="text-center p-2 rounded bg-green-100">
          <p class="text-2xl font-bold text-green-800">{{ data.stats.completed }}</p>
          <p class="text-xs text-gray-600">Completed Bid</p>
        </div>
        <div class="text-center p-2 rounded bg-amber-100">
          <p class="text-2xl font-bold text-amber-800">{{ data.stats.not_completed }}</p>
          <p class="text-xs text-gray-600">Pending</p>
        </div>
      </div>
      <div class="overflow-x-auto">
        <table class="w-full text-sm">
          <thead>
            <tr class="border-b border-gray-300">
              <th class="text-left py-2 font-medium">Invited To</th>
              <th class="text-left py-2 font-medium">Type</th>
              <th class="text-left py-2 font-medium">Status</th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let inv of data.stats.invited_details" class="border-b border-gray-200">
              <td class="py-2">
                <span class="font-medium">{{ inv.name }}</span>
                <span *ngIf="inv.email && inv.type === 'org'" class="text-gray-500 text-xs ml-1">({{ inv.email }})</span>
                <span *ngIf="inv.type === 'guest'" class="text-gray-500 text-xs ml-1">({{ inv.email }})</span>
              </td>
              <td class="py-2">
                <span *ngIf="inv.type === 'org'" class="px-2 py-0.5 text-xs rounded bg-gray-200">Organization</span>
                <span *ngIf="inv.type === 'guest'" class="px-2 py-0.5 text-xs rounded bg-purple-100">Guest (Email)</span>
              </td>
              <td class="py-2">
                <span *ngIf="inv.completed" class="px-2 py-0.5 text-xs font-medium rounded bg-green-100 text-green-800">✓ Completed</span>
                <span *ngIf="!inv.completed" class="px-2 py-0.5 text-xs font-medium rounded bg-amber-100 text-amber-800">⏳ Pending</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Email & closing actions (only when bid is not closed) -->
    <div *ngIf="data.invitation?.status !== 'closed'" class="mb-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
      <h3 class="font-semibold text-gray-800 mb-3 flex items-center gap-2">
        <mat-icon style="font-size:20px;width:20px;height:20px">email</mat-icon>
        Email & closing
      </h3>
      <div class="flex flex-wrap gap-2 mb-2">
        <button mat-stroked-button color="primary" (click)="sendBidReminder()" [disabled]="reminderSending || (data.stats?.not_completed || 0) === 0">
          <mat-icon class="mr-1" style="font-size:18px;width:18px;height:18px">notifications_active</mat-icon>
          {{ reminderSending ? 'Sending...' : 'Send reminder (pending)' }}
        </button>
        <button mat-stroked-button color="primary" (click)="openExtendClosingDialog()" [disabled]="extendSending">
          <mat-icon class="mr-1" style="font-size:18px;width:18px;height:18px">schedule</mat-icon>
          {{ extendSending ? 'Extending...' : 'Extend closing' }}
        </button>
        <button mat-stroked-button color="warn" (click)="openCloseBidDialog()" [disabled]="closeBidSending">
          <mat-icon class="mr-1" style="font-size:18px;width:18px;height:18px">block</mat-icon>
          {{ closeBidSending ? 'Closing...' : 'Close bid & notify all' }}
        </button>
      </div>
      <p class="text-xs text-gray-500" *ngIf="(data.stats?.not_completed || 0) > 0">Reminder goes to {{ data.stats.not_completed }} pending; Extend/Close emails all invitees.</p>
    </div>

    <!-- Post-approval actions (shown when any bid is approved) -->
    <div *ngIf="hasApprovedBid()" class="mb-4 p-4 bg-green-50 rounded-lg border border-green-200">
      <p class="text-sm font-medium text-green-800 mb-3">Bid approved. Next steps:</p>
      <div class="flex flex-wrap gap-2">
        <button mat-raised-button color="accent" (click)="openAwardBiddersDialog()">
          <mat-icon class="mr-1" style="font-size:18px;width:18px;height:18px">gavel</mat-icon>
          Award to Bidders
        </button>
        <button mat-stroked-button color="primary" (click)="goToSurvey()">
          <mat-icon class="mr-1" style="font-size:18px;width:18px;height:18px">location_on</mat-icon>
          Survey
        </button>
        <button mat-stroked-button color="primary" (click)="goToWorkBreakdownStructure()">
          <mat-icon class="mr-1" style="font-size:18px;width:18px;height:18px">account_tree</mat-icon>
          Create Work Breakdown Structure
        </button>
        <button mat-stroked-button color="primary" (click)="goToResourceMobilization()">
          <mat-icon class="mr-1" style="font-size:18px;width:18px;height:18px">groups</mat-icon>
          Resource Mobilization
        </button>
      </div>
    </div>

    <div *ngIf="data.responses?.length === 0" class="py-8 text-center text-gray-500">
      <mat-icon class="text-5xl mb-2">inbox</mat-icon>
      <p>No bids submitted yet</p>
    </div>

    <div *ngIf="data.responses?.length" class="space-y-4">
      <div *ngFor="let bid of data.responses" class="border rounded-lg p-4 hover:bg-gray-50" [ngClass]="{'bg-green-50': bid.status === 'shortlisted', 'bg-red-50': bid.status === 'rejected'}">
        <div class="flex justify-between items-start mb-2">
          <div class="flex items-center gap-2">
            <h3 class="font-semibold text-gray-900">{{ bid.org_name }} <span *ngIf="bid.bidder_type === 'guest'" class="text-xs font-normal text-gray-500">(via email)</span></h3>
            <span *ngIf="bid.status === 'shortlisted'" class="px-2 py-0.5 text-xs font-medium rounded bg-green-100 text-green-800">Approved</span>
            <span *ngIf="bid.status === 'rejected'" class="px-2 py-0.5 text-xs font-medium rounded bg-red-100 text-red-800">Declined</span>
          </div>
          <span class="text-lg font-bold text-primary">{{ formatCurrency(bid.bid_amount) }}</span>
        </div>
        <div *ngIf="bid.bid_data?.items?.length" class="mt-2 overflow-x-auto">
          <table class="w-full text-xs">
            <thead><tr class="border-b"><th class="text-left py-1">Item</th><th class="text-right py-1">Est. Qty</th><th class="text-right py-1">Est. Rate</th><th class="text-right py-1">Bid Qty</th><th class="text-right py-1">Bid Rate</th><th class="text-right py-1">Amount</th></tr></thead>
            <tbody>
              <tr *ngFor="let i of bid.bid_data.items" class="border-b">
                <td class="py-1">{{ i.item_description }}</td>
                <td class="py-1 text-right">{{ i.estimated_quantity ?? i.quantity }}</td>
                <td class="py-1 text-right">{{ formatCurrency(i.estimated_unit_rate ?? i.bid_rate) }}</td>
                <td class="py-1 text-right">{{ i.quantity }}</td>
                <td class="py-1 text-right">{{ formatCurrency(i.bid_rate) }}</td>
                <td class="py-1 text-right">{{ formatCurrency(i.line_total) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p *ngIf="bid.remarks" class="text-sm text-gray-600 mt-2">{{ bid.remarks }}</p>
        <div class="flex justify-between items-center mt-2">
          <p class="text-xs text-gray-500">Submitted: {{ bid.created_at | date: 'medium' }}</p>
          <div *ngIf="bid.status === 'submitted'" class="flex gap-2">
            <button mat-stroked-button color="primary" (click)="updateBidStatus(bid, 'shortlisted')" [disabled]="bid.updating">
              <mat-icon class="mr-1" style="font-size:18px;width:18px;height:18px">check_circle</mat-icon>
              Approve
            </button>
            <button mat-stroked-button color="warn" (click)="updateBidStatus(bid, 'rejected')" [disabled]="bid.updating">
              <mat-icon class="mr-1" style="font-size:18px;width:18px;height:18px">cancel</mat-icon>
              Decline
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Queries from Bidders -->
    <div *ngIf="data.queries?.length" class="mt-6 pt-6 border-t">
      <h3 class="font-semibold mb-3">Queries from Bidders</h3>
      <div *ngFor="let q of data.queries" class="border rounded-lg p-4 mb-3">
        <p class="text-sm font-medium text-gray-700">{{ q.org_name }}</p>
        <p class="text-sm text-gray-800 mt-1">{{ q.query_text }}</p>
        <p class="text-xs text-gray-500 mt-1">{{ q.created_at | date: 'short' }}</p>
        <div *ngIf="q.reply_text" class="mt-3 p-2 bg-green-50 rounded">
          <p class="text-sm text-gray-700">{{ q.reply_text }}</p>
          <p class="text-xs text-gray-500 mt-1">Replied: {{ q.replied_at | date: 'short' }}</p>
        </div>
        <div *ngIf="!q.reply_text" class="mt-3">
          <textarea [(ngModel)]="replyTexts[q.id]" rows="2" placeholder="Type your reply..." class="w-full border rounded p-2 text-sm"></textarea>
          <button mat-stroked-button color="primary" class="mt-2" (click)="replyToQuery(q)" [disabled]="!replyTexts[q.id]?.trim()">
            Reply
          </button>
        </div>
      </div>
    </div>
  </div>

  <div mat-dialog-actions class="flex justify-end pt-4">
    <button mat-raised-button (click)="onClose()">Close</button>
  </div>
</div>
